/**
 * Shared scope-key helper for tenant-scoped in-memory client caches.
 *
 * Persistent or byte-bearing caches require both the authenticated principal
 * and the complete, validated physical storage scope. Partial and legacy
 * scopes deliberately produce no key.
 */

export interface TenantScopeKeyInput {
    principalId?: string | null;
    companyId?: string | null;
    projectId?: string | null;
    /** Sorted server-granted capabilities for authorization-sensitive caches. */
    authorizationKey?: string | null;
}

const TENANT_CONTENT_KEY_VERSION = "tenant-content-v2";
const PRINCIPAL_DIMENSION = "stytch-principal";
const COMPANY_DIMENSION = "company";
const PROJECT_DIMENSION = "project";
const AUTHORIZATION_DIMENSION = "authorization";

/**
 * Returns `null` until principal, company, project, and the exact
 * server-granted capability set are all known. Company/project ids are
 * authoritative identifiers; the mutable Stytch organization slug is not a
 * storage or authorization boundary and deliberately is not part of this key.
 */
export function buildScopeKey({
    principalId,
    companyId,
    projectId,
    authorizationKey,
}: TenantScopeKeyInput): string | null {
    if (
        !principalId ||
        !companyId ||
        !projectId ||
        authorizationKey === null ||
        authorizationKey === undefined
    ) {
        return null;
    }
    return JSON.stringify([
        TENANT_CONTENT_KEY_VERSION,
        [PRINCIPAL_DIMENSION, principalId],
        [COMPANY_DIMENSION, companyId],
        [PROJECT_DIMENSION, projectId],
        [AUTHORIZATION_DIMENSION, authorizationKey],
    ]);
}

/** Stable fingerprint for the effective server-granted capabilities. */
export function buildCapabilityFingerprint(capabilities: Iterable<string>): string {
    return JSON.stringify(Array.from(new Set(capabilities)).sort());
}
